import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userManagementService, PlatformUser } from '../services/userManagement';
import './UserDetailPage.css';

type Tab = 'overview' | 'bookings' | 'payments' | 'reviews';

export const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<PlatformUser | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookings, setBookings] = useState<unknown[]>([]);
  const [payments, setPayments] = useState<unknown[]>([]);
  const [reviews, setReviews] = useState<unknown[]>([]);
  const [loadingTab, setLoadingTab] = useState(false);

  useEffect(() => {
    if (!userId) return;

    const loadUser = async () => {
      try {
        setLoading(true);
        setError(null);
        const userData = await userManagementService.getUser(userId);
        setUser(userData);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load user');
        console.error('Error loading user:', err);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [userId]);

  useEffect(() => {
    if (!userId || !user || activeTab === 'overview') return;

    const loadTabData = async () => {
      try {
        setLoadingTab(true);
        switch (activeTab) {
          case 'bookings': {
            const bookingsData = await userManagementService.getUserBookings(userId);
            setBookings(bookingsData.bookings);
            break;
          }
          case 'payments': {
            const paymentsData = await userManagementService.getUserPayments(userId);
            setPayments(paymentsData.payments);
            break;
          }
          case 'reviews': {
            const reviewsData = await userManagementService.getUserReviews(userId);
            setReviews(reviewsData.reviews);
            break;
          }
        }
      } catch (err: unknown) {
        console.error(`Error loading ${activeTab}:`, err);
      } finally {
        setLoadingTab(false);
      }
    };

    loadTabData();
  }, [activeTab, userId, user]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const calculateStats = () => {
    if (!user) return { totalBookings: 0, totalSpent: 0, averageRating: 0 };

    const totalBookings = bookings.length;
    const totalSpent = (payments as Array<{ amount?: number }>).reduce((sum, payment) => {
      return sum + (payment.amount || 0);
    }, 0);
    const ratings = (reviews as Array<{ rating?: number }>).map((review) => review.rating || 0);
    const averageRating =
      ratings.length > 0 ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0;

    return { totalBookings, totalSpent, averageRating };
  };

  if (loading) {
    return <div className="loading">Loading user...</div>;
  }

  if (error || !user) {
    return (
      <div className="error-container">
        <div className="error-message">{error || 'User not found'}</div>
        <button onClick={() => navigate('/dashboard/users')} className="back-button">
          Back to Users
        </button>
      </div>
    );
  }

  const stats = calculateStats();

  return (
    <div className="user-detail-page">
      <div className="user-detail-header">
        <button onClick={() => navigate('/dashboard/users')} className="back-button">
          ← Back to Users
        </button>
        <h1>User Details</h1>
      </div>

      <div className="user-info-card">
        <div className="user-info-header">
          <div>
            <h2>{user.name}</h2>
            <p className="user-email">{user.email}</p>
          </div>
          <span className={`user-type-badge ${user.userType}`}>{user.userType}</span>
        </div>

        <div className="user-stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.totalBookings}</div>
            <div className="stat-label">Total Bookings</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">₹{stats.totalSpent.toLocaleString()}</div>
            <div className="stat-label">Total Spent</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {stats.averageRating > 0 ? stats.averageRating.toFixed(1) : 'N/A'}
            </div>
            <div className="stat-label">Average Rating</div>
          </div>
        </div>

        <div className="user-details-grid">
          <div className="detail-item">
            <label>Phone</label>
            <div>{user.phone}</div>
          </div>
          <div className="detail-item">
            <label>User Type</label>
            <div className="user-type-badge">{user.userType}</div>
          </div>
          <div className="detail-item">
            <label>Created</label>
            <div>{formatDate(user.createdAt)}</div>
          </div>
          <div className="detail-item">
            <label>Last Updated</label>
            <div>{formatDate(user.updatedAt)}</div>
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`tab ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => setActiveTab('bookings')}
          >
            Bookings ({bookings.length})
          </button>
          <button
            className={`tab ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            Payments ({payments.length})
          </button>
          <button
            className={`tab ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            Reviews ({reviews.length})
          </button>
        </div>

        <div className="tab-content">
          {loadingTab ? (
            <div className="loading">Loading {activeTab}...</div>
          ) : (
            <>
              {activeTab === 'overview' && (
                <div className="overview-content">
                  <h3>User Information</h3>
                  <div className="info-grid">
                    <div>
                      <strong>User ID:</strong> {user.userId}
                    </div>
                    <div>
                      <strong>Email:</strong> {user.email}
                    </div>
                    <div>
                      <strong>Phone:</strong> {user.phone}
                    </div>
                    <div>
                      <strong>Name:</strong> {user.name}
                    </div>
                    {user.profileData && Object.keys(user.profileData).length > 0 && (
                      <div className="profile-data">
                        <strong>Profile Data:</strong>
                        <pre>{JSON.stringify(user.profileData, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'bookings' && (
                <div className="bookings-content">
                  <h3>User Bookings</h3>
                  {bookings.length === 0 ? (
                    <div className="empty-state">No bookings found</div>
                  ) : (
                    <div className="data-list">
                      {bookings.map((booking: Record<string, unknown>, index) => (
                        <div key={String(booking.bookingId ?? index)} className="data-item">
                          <div className="data-item-header">
                            <strong>Booking ID:</strong> {String(booking.bookingId ?? 'N/A')}
                          </div>
                          <div className="data-item-body">
                            <pre>{JSON.stringify(booking, null, 2)}</pre>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'payments' && (
                <div className="payments-content">
                  <h3>User Payments</h3>
                  {payments.length === 0 ? (
                    <div className="empty-state">No payments found</div>
                  ) : (
                    <div className="data-list">
                      {payments.map((payment: Record<string, unknown>, index) => (
                        <div key={String(payment.paymentId ?? index)} className="data-item">
                          <div className="data-item-header">
                            <strong>Payment ID:</strong> {String(payment.paymentId ?? 'N/A')}
                          </div>
                          <div className="data-item-body">
                            <pre>{JSON.stringify(payment, null, 2)}</pre>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className="reviews-content">
                  <h3>User Reviews</h3>
                  {reviews.length === 0 ? (
                    <div className="empty-state">No reviews found</div>
                  ) : (
                    <div className="data-list">
                      {reviews.map((review: Record<string, unknown>, index) => (
                        <div key={String(review.reviewId ?? index)} className="data-item">
                          <div className="data-item-header">
                            <strong>Review ID:</strong> {String(review.reviewId ?? 'N/A')}
                          </div>
                          <div className="data-item-body">
                            <pre>{JSON.stringify(review, null, 2)}</pre>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

